import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Code2, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const XmlEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  // XML entities map - focusing on the 5 predefined XML entities plus common extended ones
  const xmlEntities: Record<string, string> = {
    '&': '&amp;',      // Must be first to avoid double-escaping
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&apos;',
    // Additional common XML entities
    '\t': '&#9;',
    '\n': '&#10;',
    '\r': '&#13;',
    ' ': '&#32;',      // For preserving spaces in XML
    '©': '&#169;',
    '®': '&#174;',
    '™': '&#8482;',
    '€': '&#8364;',
    '£': '&#163;',
    '¥': '&#165;',
    '§': '&#167;',
    '¶': '&#182;',
    '°': '&#176;',
    '±': '&#177;',
    '÷': '&#247;',
    '×': '&#215;',
    'α': '&#945;',
    'β': '&#946;',
    'γ': '&#947;',
    'δ': '&#948;',
    'ε': '&#949;',
    'λ': '&#955;',
    'μ': '&#956;',
    'π': '&#960;',
    'σ': '&#963;',
    'τ': '&#964;',
    'φ': '&#966;',
    'ω': '&#969;'
  };

  const escapeXml = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Process entities in specific order to avoid conflicts
    const orderedEntities = ['&', '<', '>', '"', "'", ...Object.keys(xmlEntities).filter(k => !['&', '<', '>', '"', "'"].includes(k))];
    
    orderedEntities.forEach((char) => {
      const entity = xmlEntities[char];
      if (entity) {
        const regex = new RegExp(char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        const matches = result.match(regex);
        if (matches) {
          entitiesCount += matches.length;
          result = result.replace(regex, entity);
        }
      }
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeXml = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Handle named entities
    Object.entries(xmlEntities).forEach(([char, entity]) => {
      const regex = new RegExp(entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Handle numeric decimal entities (&#123;)
    result = result.replace(/&#(\d+);/g, (match, dec) => {
      const code = parseInt(dec, 10);
      if (code >= 0 && code <= 1114111) { // Valid Unicode range
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match; // Return original if invalid
    });

    // Handle numeric hexadecimal entities (&#x1A2B;)
    result = result.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 1114111) { // Valid Unicode range
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match; // Return original if invalid
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeXml : unescapeXml;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeXml, unescapeXml]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-xml.xml`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleXml = (): void => {
    setInputText(`<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <book id="1">
    <title>Learning XML & XSLT</title>
    <author>John O'Reilly</author>
    <description>A comprehensive guide to XML processing with "real-world" examples.</description>
    <price currency="USD">29.99</price>
    <tags>
      <tag>XML</tag>
      <tag>Programming</tag>
      <tag>Web Development</tag>
    </tags>
    <!-- Special characters: < > & " ' -->
    <notes>Copyright © 2024. All rights reserved.</notes>
  </book>
</catalog>`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`&lt;?xml version=&quot;1.0&quot; encoding=&quot;UTF-8&quot;?&gt;
&lt;catalog&gt;
  &lt;book id=&quot;1&quot;&gt;
    &lt;title&gt;Learning XML &amp; XSLT&lt;/title&gt;
    &lt;author&gt;John O&apos;Reilly&lt;/author&gt;
    &lt;description&gt;A comprehensive guide to XML processing with &quot;real-world&quot; examples.&lt;/description&gt;
    &lt;price currency=&quot;USD&quot;&gt;29.99&lt;/price&gt;
    &lt;tags&gt;
      &lt;tag&gt;XML&lt;/tag&gt;
      &lt;tag&gt;Programming&lt;/tag&gt;
      &lt;tag&gt;Web Development&lt;/tag&gt;
    &lt;/tags&gt;
    &lt;!-- Special characters: &amp;lt; &amp;gt; &amp;amp; &amp;quot; &amp;apos; --&gt;
    &lt;notes&gt;Copyright &#169; 2024. All rights reserved.&lt;/notes&gt;
  &lt;/book&gt;
&lt;/catalog&gt;`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg">
              <Code2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">XML Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced XML entity processor with TypeScript analytics</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-green-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape XML</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape XML</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Entities</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Entity Breakdown */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported XML Entities</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                <div className="text-gray-300">Core: &amp; &lt; &gt; &quot; &apos;</div>
                <div className="text-gray-300">Whitespace: &#9; &#10; &#13;</div>
                <div className="text-gray-300">Symbols: © ® ™ € £</div>
                <div className="text-gray-300">Math: ° ± ÷ ×</div>
                <div className="text-gray-300">Greek: α β γ δ π ω</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xml,.txt,.html,.htm"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter XML to escape...' : 'Enter escaped XML to unescape...'}
              className="flex-1 bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none resize-none font-mono text-sm"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed XML output will appear here..."
              className="flex-1 bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleXml}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample XML
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default XmlEscapeUnescapeTool;